import { useEffect, useRef } from 'react'

import { DisplayFieldSkeleton } from 'components/ui/inputs/display-field'
import { ScrollArea } from 'components/ui/scroll-area'
import * as TabsPrimitive from 'components/ui/tabs'
import useEcpTranslation from 'hooks/common/useEcpTranslation'
import haptics from 'utils/haptics'
import { getCurrentLanguage } from 'utils/LanguageUtils'
import { cn } from 'utils/StyleUtils'

type TabsProps = {
  className?: string
  children: React.ReactNode
  onValueChange: (value: string) => void
  value: string
}

export const TabContainer = ({
  onValueChange,
  children,
  value,
  className,
  ...props
}: TabsProps) => {
  return (
    <TabsPrimitive.Tabs
      className={cn('w-full', className)}
      onValueChange={(value: string) => {
        haptics.light()
        onValueChange(value)
      }}
      value={value}
      {...props}
    >
      {children}
    </TabsPrimitive.Tabs>
  )
}

type Tab = {
  id: string
  hint?: React.ReactNode
  label: string | Record<string, string>
}

type TabListProps = {
  tabs: Tab[]
  className?: string
}

export const TabList = ({ tabs, ...props }: TabListProps) => {
  const { t } = useEcpTranslation()
  const tabListRef = useRef<HTMLDivElement>(null)
  const language = getCurrentLanguage()

  const scrollIntoView = () => {
    const activeTab: HTMLButtonElement =
      tabListRef?.current?.querySelector?.(`[aria-selected=true]`)

    if (activeTab) {
      activeTab.scrollIntoView({
        behavior: 'instant' as ScrollBehavior,
        block: 'nearest',
        inline: 'center'
      })
    }
  }

  useEffect(() => {
    scrollIntoView()
  }, [])

  return (
    <ScrollArea className="pb-1.5" direction="horizontal" type="hover">
      <TabsPrimitive.TabsList
        className="flex w-fit gap-4 text-gray"
        ref={tabListRef}
        {...props}
      >
        {tabs.map((tab) => {
          const tabLabel = t(tab.label?.[language] ?? tab.label)

          return (
            <TabsPrimitive.TabsTrigger
              className="min-w-20 text-base flex font-bold last:mr-6 focus-visible:ring first:ml-0.5"
              data-block-id={tab.id}
              key={`tab-${tab.id}`}
              onClick={() => scrollIntoView()}
              value={tab.id}
            >
              <div className="flex gap-2">
                <h2 className="text-nowrap">{tabLabel}</h2>
                {tab.hint}
              </div>
              <span className="mt-1.5 flex h-1 w-full px-4 in-data-[state=active]:bg-base-accent-solid" />
            </TabsPrimitive.TabsTrigger>
          )
        })}
      </TabsPrimitive.TabsList>
    </ScrollArea>
  )
}

type TabsContentProps = {
  children: React.ReactNode
  value: Tab['id']
  className?: string
}

export const TabContent = ({
  children,
  value,
  className
}: TabsContentProps) => {
  return (
    <TabsPrimitive.TabsContent
      className={cn(
        'flex w-full flex-col gap-4 rounded-sm focus-visible:ring',
        className
      )}
      tabIndex={-1}
      value={value}
    >
      {children}
    </TabsPrimitive.TabsContent>
  )
}

export const TabsSkeleton = () => {
  return (
    <div className="flex w-full flex-col gap-4" data-testid="tabs-skeleton">
      <TabListSkeleton />
      <TabContentSkeleton />
    </div>
  )
}

const TabContentSkeleton = () => (
  <div className="flex flex-col gap-4">
    <DisplayFieldSkeleton data-testid="skeleton" />
    <DisplayFieldSkeleton data-testid="skeleton" />
    <DisplayFieldSkeleton data-testid="skeleton" />
  </div>
)

const TabListSkeleton = () => {
  return (
    <div className="h-10 items-center justify-center flex w-fit gap-6 text-gray">
      <div className="flex flex-col gap-1 h-8">
        <div className="flex gap-1.25 h-6">
          <div className="w-23 h-5 animate-pulse rounded-md bg-base-gray-skeleton" />
        </div>
        <span className="flex h-1 w-full px-4 bg-base-accent-skeleton animate-pulse" />
      </div>

      <div className="flex flex-col gap-1 h-8">
        <div className="flex gap-1.25 h-6">
          <div className="w-23 h-5 animate-pulse rounded-md bg-base-gray-skeleton" />
        </div>

        <span className="flex h-1 w-full px-4 bg-transparent" />
      </div>

      <div className="flex flex-col gap-1 h-8">
        <div className="flex gap-1.25 h-6">
          <div className="w-23 h-5 animate-pulse rounded-md bg-base-gray-skeleton" />
        </div>

        <span className="flex h-1 w-full px-4 bg-transparent" />
      </div>
    </div>
  )
}
